import React, { useState, useContext, useEffect, useRef } from "react";
import styled from "styled-components";
import { SunsetAPIAccountContext } from "../../Networking/Interface/Account/AccountAPIProvider";
import { ToastType } from "../../Components/Toast/Toast";
import { useToast } from "../../Components/Toast/ToastContext";
import { Button, Input } from "antd";
import { Wealth } from "../../Networking/Interface/Account/AccountInterface";

const Container = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
`;

const InputBox = styled(Input)`
  display: flex;
  align-items: center;
  margin-left: 15px;
  width: 150px;
  margin-right: 1px;
`;

interface SetCashButtonProps {
  characterName: string;  // Updated to use character name
  userId: number;         // Add userId prop for setting cash
  setWealth: (wealth: Wealth) => void; // Prop to update wealth
  onSuccess?: () => void;
}

const SetCashButton: React.FC<SetCashButtonProps> = ({ characterName, userId, setWealth, onSuccess }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [cash, setCash] = useState<string>("");
  const { showToast } = useToast();
  const api = useContext(SunsetAPIAccountContext);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsEditing(false);
        setCash(""); // Clear the input field
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSubmit = async () => {
    const amount = parseFloat(cash);

    if (isNaN(amount) || amount < 0) {
      showToast(ToastType.Error, "Invalid cash amount", 5);
      return;
    }

    try {
   
      const response = await api?.setCash(userId, amount.toString()); // Use userId and convert cash to string
      if (!response?.success) {
        throw new Error("Failed to set cash amount");
      }
      showToast(ToastType.Success, "Cash amount set successfully", 5);
      
      // Fetch updated wealth and set it
      const wealthResponse = await api?.getWealth(characterName); // Use characterName
      if (wealthResponse?.success) {
        setWealth(wealthResponse.data!);
      }

      setIsEditing(false); // Close the input field after successful submission
      setCash(""); // Clear the input field
      if (onSuccess) {
        onSuccess(); // Call onSuccess if provided
      }
    } catch (error) {
      console.error("Error setting cash amount:", error);
      showToast(ToastType.Error, "Failed to set cash amount", 5);
    }
  };

  return (
    <Container ref={containerRef}>
      {isEditing ? (
        <>
          <InputBox
            placeholder="Cash Amount"
            value={cash}
            onChange={(e) => setCash(e.target.value)}
            type="number"
          />
          <Button style={{ border: "none" }} onClick={handleSubmit}>
            Set
          </Button>
        </>
      ) : (
        <Button style={{ border: "none" }} onClick={() => setIsEditing(true)}>
          Set Cash Amount
        </Button>
      )}
    </Container>
  );
};

export default SetCashButton;
